/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import { useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { openSecuritySetup, Outcome } from '../utils/loaElevate.util';
import { showToast } from '../AppSlice/AppSlice';
import { fetchLoaInstance } from '../../../features/Profile/LoAFactors/profileEnrollSlice';
import { LoaInstanceResult } from '../../../infrastructure/repositories/ILoaInstanceRepository';
import { I18nNamespaces, LoaElevationAction } from '../utils/constants.util';
import { getConfig } from '../utils/config.util';

const outcomeToastKeys: Record<Exclude<Outcome, 'success' | 'redirecting'>, string> = {
  aborted: 'loaEnrichment.common.loaElevation.outcomeAborted',
  failed: 'loaEnrichment.common.loaElevation.outcomeFailed',
  timeout: 'loaEnrichment.common.loaElevation.outcomeTimeout',
  occupied: 'loaEnrichment.common.loaElevation.outcomeOccupied'
};

interface UseLoaElevationReturn {
  showModal: boolean;
  action: LoaElevationAction;
  elevating: boolean;
  requireElevation: (
    action: LoaElevationAction,
    callback: () => void,
    options?: { redirectParams?: Record<string, string>; resourceName?: string }
  ) => void;
  handleContinue: () => Promise<void>;
  handleCancel: () => void;
}

export interface UseLoaElevationOptions {
  fetchInstance?: () => Promise<LoaInstanceResult>;
}

// eslint-disable-next-line import/prefer-default-export
export const useLoaElevation = (
  options?: UseLoaElevationOptions
): UseLoaElevationReturn => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const ssoEnabled = useSelector((s: RootState) => s.profile?.profileDetails?.ssoEnabled);
  const fetchInstanceFn = options?.fetchInstance
    ?? (() => fetchLoaInstance('factorsModify'));

  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState<LoaElevationAction>('add');
  const [elevating, setElevating] = useState(false);
  const pendingCallback = useRef<(() => void) | null>(null);
  const pendingRedirectParams = useRef<Record<string, string> | undefined>(undefined);
  const pendingResourceName = useRef<string | undefined>(undefined);

  const requireElevation = useCallback((
    elevationAction: LoaElevationAction,
    callback: () => void,
    options?: { redirectParams?: Record<string, string>; resourceName?: string }
  ) => {
    const { ssoUsersLoaGate, nonSsoUsersLoaGate } = getConfig();
    const gateEnabled = ssoEnabled
      ? String(ssoUsersLoaGate).toLowerCase() !== 'false'
      : String(nonSsoUsersLoaGate).toLowerCase() !== 'false';

    if (!gateEnabled) {
      callback();
      return;
    }

    pendingCallback.current = callback;
    pendingRedirectParams.current = options?.redirectParams;
    pendingResourceName.current = options?.resourceName;
    setAction(elevationAction);
    setShowModal(true);
  }, [ssoEnabled]);

  const handleContinue = useCallback(async () => {
    setElevating(true);

    let instanceId: string | undefined;
    let level: number;
    try {
      const result = await fetchInstanceFn();
      instanceId = result.instance;
      level = result.level;
    } catch {
      setElevating(false);
      setShowModal(false);
      pendingCallback.current = null;
      pendingRedirectParams.current = undefined;
      dispatch(showToast({
        title: t('loaEnrichment.common.loaElevation.instanceFetchFailed'),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
      return;
    }

    const outcome: Outcome = await openSecuritySetup({
      loaLevel: level,
      origin: window.location.origin,
      applicationId: getConfig().appId,
      fallback: 'same-tab',
      dropPathSegments: 2,
      instance: instanceId,
      redirectParams: pendingRedirectParams.current,
      resourceName: pendingResourceName.current,
      onPopupOpened: () => setShowModal(false)
    });

    if (outcome === 'redirecting') return;

    setElevating(false);

    if (outcome === 'success') {
      setShowModal(false);
      const cb = pendingCallback.current;
      pendingCallback.current = null;
      pendingRedirectParams.current = undefined;
      pendingResourceName.current = undefined;
      if (cb) cb();
    } else {
      setShowModal(false);
      pendingCallback.current = null;
      pendingRedirectParams.current = undefined;
      pendingResourceName.current = undefined;
      if (outcome === 'failed') {
        dispatch(showToast({
          title: t(outcomeToastKeys[outcome]),
          timeout: 0 as number,
          kind: 'error',
          subtitle: ''
        }) as any);
      } else {
        dispatch(showToast({
          title: t(outcomeToastKeys[outcome]),
          timeout: 0 as number,
          kind: 'info',
          subtitle: ''
        }) as any);
      }
    }
  }, [dispatch, t]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    pendingCallback.current = null;
    pendingRedirectParams.current = undefined;
    pendingResourceName.current = undefined;
  }, []);

  return {
    showModal,
    action,
    elevating,
    requireElevation,
    handleContinue,
    handleCancel
  };
};
